import { useState, useEffect, useRef } from 'react'
import { useSnapshot } from 'valtio'
import { state } from './App'
import { useSectionsStore } from './store'

export function ProjectInfo() {
  const { clicked } = useSnapshot(state)
  const { projectData, horizontalProgress, isInStudio, shouldHideTitlesDuringTransition } = useSectionsStore()
  const [isVisible, setIsVisible] = useState(false)
  const [currentProject, setCurrentProject] = useState(null)
  const [isAnimating, setIsAnimating] = useState(false)
  const isTransitioning = useRef(false)
  const currentTimeout = useRef(null)
  
  // Effect to handle project selection and showing
  useEffect(() => {
    // Clear any existing timeout
    if (currentTimeout.current) {
      clearTimeout(currentTimeout.current)
      currentTimeout.current = null
    }
    
    if (clicked !== null && projectData && projectData[clicked]) {
      const project = projectData[clicked]
      
      setCurrentProject(project)
      setIsVisible(false)
      setIsAnimating(false)
      isTransitioning.current = true
      
      // Start animation after delay (like space.js animateIn with delay)
      currentTimeout.current = setTimeout(() => {
        setIsVisible(true)
        setIsAnimating(true)
        isTransitioning.current = false
      }, 350)
      
      return () => {
        if (currentTimeout.current) {
          clearTimeout(currentTimeout.current)
          currentTimeout.current = null
        }
      }
    } else {
      // Animate out - always use smooth fade for photo/video changes
      setIsVisible(false)
      setIsAnimating(false)
      isTransitioning.current = true
      
      currentTimeout.current = setTimeout(() => {
        setCurrentProject(null)
        isTransitioning.current = false
      }, 1200) // Use longer timeout for smooth fade (match transform duration)
      
      return () => {
        if (currentTimeout.current) {
          clearTimeout(currentTimeout.current)
          currentTimeout.current = null
        }
      }
    }
  }, [clicked, projectData])
  
  // Effect to handle section changes and fade out when appropriate
  useEffect(() => {
    // Don't interfere if we're already transitioning
    if (isTransitioning.current) return
    
    if (currentProject) {
      // Check if we should hide during video scaling transition
      const hideDuringTransition = shouldHideTitlesDuringTransition();
      
      // Fade out when going to splash, studio, or during transitions
      if (horizontalProgress < 0.05 || isInStudio || horizontalProgress >= 0.85 || hideDuringTransition) {
        console.log('ProjectInfo: Fading out due to:', {
          isInStudio,
          horizontalProgress: horizontalProgress.toFixed(3),
          hideDuringTransition,
          isGoingToSplash: horizontalProgress < 0.05
        })
        
        setIsVisible(false);
        setIsAnimating(false);
        isTransitioning.current = true;
        
        // Clear any existing timeout
        if (currentTimeout.current) {
          clearTimeout(currentTimeout.current)
        }
        
        // Always use smooth timeout - remove the quick timeout logic
        currentTimeout.current = setTimeout(() => {
          if (!isVisible) {
            setCurrentProject(null)
          }
          isTransitioning.current = false
        }, 1200); // Always use 1200ms to match the smooth transform duration
        
        return () => {
          if (currentTimeout.current) {
            clearTimeout(currentTimeout.current)
            currentTimeout.current = null
          }
        }
      } else if (clicked !== null && !isVisible && !isTransitioning.current) {
        // Fade back in if we have a project and should be visible (and not already transitioning)
        setIsVisible(true);
        setIsAnimating(true);
      }
    }
  }, [horizontalProgress, isInStudio, currentProject, isVisible, shouldHideTitlesDuringTransition, clicked])
  
  if (!currentProject) {
    return null;
  }
  
  // Always use smooth fade for photo/video selections
  // Only use quick fade when specifically going to splash screen
  const isGoingToSplash = horizontalProgress < 0.05 && !isVisible && currentProject;
  
  return (
    <div className="project-info-container">
      <div 
        className={`project-info-content ${isVisible ? 'active' : ''} ${isAnimating ? 'animating' : ''}`}
        style={{
          opacity: isVisible ? 1 : 0,
          transform: isVisible ? 'translateY(0px)' : 'translateY(10px)',
          transitionProperty: 'opacity, transform',
          transitionDuration: isVisible ? '800ms, 1200ms' : '600ms, 800ms',
          transitionTimingFunction: isVisible 
            ? 'cubic-bezier(0.445, 0.05, 0.55, 0.95), cubic-bezier(0.215, 0.61, 0.355, 1)'
            : 'cubic-bezier(0.215, 0.61, 0.355, 1), cubic-bezier(0.215, 0.61, 0.355, 1)',
          transitionDelay: '0ms, 0ms'
        }}
      >
        <h3 className="project-info-title">
          {currentProject.title}
        </h3>
        
        <p className="project-info-description">
          {currentProject.description}
        </p>
        
        {currentProject.link && (
          <a 
            href={currentProject.link}
            target="_blank"
            rel="noopener noreferrer"
            className="project-info-link"
          >
            Learn More →
          </a>
        )}
      </div>
    </div>
  )
} 